import classNames from "classnames";
import { forwardRef, useEffect, useRef, useState } from "react";
import { CheckActiveIcon, CrossActiveIcon } from "../../AscIcons";
import AscTextInput from "../../AscInputFields/AscTextInput/AscTextInput";
import AscGenericCard from "../AscGenericCard/AscGenericCard";
import styles from "./AscFlashCard.module.scss";

export type AscFlashCardState = "default" | "correct" | "incorrect";

export interface AscFlashCardProps {
  id?: string;
  title: string;
  coverImageUrl?: string;
  focused?: boolean;
  state?: AscFlashCardState;
  correctAnswer?: string;
  onClick?: () => void;
  onAnswerChange?: (id: string, answer: string) => void;
  answerLoading?: boolean;
  loading?: boolean;
  className?: string;
  disabled?: boolean;
  answer?: string;
}

const AscFlashCard = forwardRef<HTMLDivElement, AscFlashCardProps>(
  (
    {
      title,
      coverImageUrl,
      state = "default",
      focused = false,
      correctAnswer,
      onClick,
      id,
      loading = false,
      answerLoading = false,
      className,
      disabled = false,
      answer: answerProp,
      onAnswerChange,
    },
    ref
  ) => {
    const [answer, setAnswer] = useState<string>("");
    const inputRef = useRef<HTMLInputElement>(null);

    function handleAnswerChange(event: React.ChangeEvent<HTMLInputElement>) {
      if (disabled) {
        return;
      }
      setAnswer(event.target.value);
      if (id) {
        onAnswerChange?.(id, event.target.value);
      }
    }

    useEffect(() => {
      if (focused && inputRef?.current) {
        inputRef.current.focus();
      }
    }, [focused]);

    useEffect(() => {
      setAnswer(answerProp || "");
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
      //reset answer when answer prop is empty
      if (!answerProp) {
        setAnswer("");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answerProp]);
    return (
      <AscGenericCard
        onClick={onClick}
        loading={loading}
        ref={ref}
        className={classNames(
          styles.ground,
          {
            [styles.focused]: !!focused,
            [styles.default]: state === "default",
            [styles.correct]: state === "correct",
            [styles.incorrect]: state === "incorrect",
          },
          className
        )}
        imageClassName={styles.cardImage}
        titleClassName={styles.cardTitle}
        bodyClassName={styles.cardBody}
        title={title}
        coverImageUrl={coverImageUrl}
        footerClassName={styles.cardFooter}
        footer={
          <div className={styles.footerContent}>
            <AscTextInput
              ref={inputRef}
              className={styles.input}
              placeholder="Answer"
              value={answer}
              onChange={handleAnswerChange}
              disabled={disabled || state !== "default"}
            />
          </div>
        }
      >
        <div className={styles.maskContainer}>
          <CheckActiveIcon
            data-testid="asc-flash-card-correct-icon"
            className={styles.correctIcon}
          />
          <CrossActiveIcon
            data-testid="asc-flash-card-incorrect-icon"
            className={styles.incorrectIcon}
          />
          <span className={styles.correctAnswer}>
            Correct Answer: {correctAnswer}
          </span>
        </div>
      </AscGenericCard>
    );
  }
);

export default AscFlashCard;
